/**
 * Single-best ghost suggestion for the Quick Add input.
 * Pure: (input, grammar pack, usage rankings) → suggestion or null.
 */

import {
  buildLabelIndex,
  findContract,
  isTenorPast,
  normalizeTenorToken,
} from "./optionShorthandParser";

// Tenor stems (months, seasons, quarters). Used both to complete tenors and
// to keep label suggestions off tokens that are likely a tenor being typed.
const TENOR_WORDS = [
  "jan", "feb", "mar", "apr", "may", "jun",
  "jul", "aug", "sep", "oct", "nov", "dec",
  "sum", "win", "cal", "q1", "q2", "q3", "q4",
];

const looksLikeTenorStart = (typed) =>
  TENOR_WORDS.some((w) => w.startsWith(typed));

/**
 * Complete a bare tenor stem to its nearest tradeable year:
 * "apr" → "apr27" when April of this year already passed, "dec" → "dec26".
 * Requires an exact stem or an unambiguous prefix of ≥2 chars ("ap" → apr).
 */
function suggestTenor(lastToken, tokensBefore, now) {
  if (tokensBefore.some((t) => normalizeTenorToken(t))) return null;
  const typed = lastToken.toLowerCase();
  if (!/^(?:[a-z]+|q[1-4])$/.test(typed)) return null;

  const matches = TENOR_WORDS.filter((w) => w.startsWith(typed));
  let stem = null;
  if (matches.includes(typed)) stem = typed;
  else if (matches.length === 1 && typed.length >= 2) stem = matches[0];
  if (!stem) return null;

  const baseYear = now.getFullYear();
  for (let year = baseYear - 1; year <= baseYear + 1; year++) {
    const yy = String(year).slice(-2);
    const normalized = normalizeTenorToken(`${stem}${yy}`);
    if (normalized && !isTenorPast(normalized, now)) {
      return stem.slice(typed.length) + yy;
    }
  }
  return null;
}

function rankIndexMap(rankedList) {
  const map = new Map();
  rankedList.forEach((item, i) => {
    if (!map.has(item.key)) map.set(item.key, i);
  });
  return map;
}

function suggestContract(lastToken, grammarPack, rankings) {
  const typed = lastToken.toLowerCase();
  const candidates = [];
  (grammarPack.contracts || []).forEach((c) => {
    [c.token, ...(c.aliases || [])].forEach((name) => {
      const label = String(name || "");
      if (
        label.length > typed.length &&
        label.toLowerCase().startsWith(typed)
      ) {
        candidates.push({ label, token: String(c.token) });
      }
    });
  });
  if (!candidates.length) return null;

  const rank = rankIndexMap(rankings.contracts);
  candidates.sort((a, b) => {
    const ra = rank.has(a.token) ? rank.get(a.token) : Infinity;
    const rb = rank.has(b.token) ? rank.get(b.token) : Infinity;
    if (ra !== rb) return ra - rb;
    return a.label.length - b.label.length;
  });
  return candidates[0].label;
}

function suggestLabel(lastToken, grammarPack, rankings) {
  // Optional glued strikes followed by an alpha label prefix: "12/24c" or "str".
  const m = lastToken.match(/^(-?[\d.]+(?:\/-?[\d.]+)*)?([a-z]+)$/i);
  if (!m || !m[2]) return null;
  const strikesPart = m[1] || "";
  const typed = m[2].toLowerCase();
  if (!strikesPart && looksLikeTenorStart(typed)) return null;

  const candidates = buildLabelIndex(grammarPack).filter(
    (e) => e.key.length > typed.length && e.key.startsWith(typed)
  );
  if (!candidates.length) return null;

  const rank = rankIndexMap(rankings.structures);
  candidates.sort((a, b) => {
    const ra = rank.has(a.key) ? rank.get(a.key) : Infinity;
    const rb = rank.has(b.key) ? rank.get(b.key) : Infinity;
    if (ra !== rb) return ra - rb;
    return a.key.length - b.key.length;
  });
  return candidates[0].shortname;
}

function suggestTemplate(value, rankings) {
  const norm = value.replace(/\s+/g, " ").trimStart().toLowerCase();
  if (!norm.trim()) return null;
  const hit = rankings.lines.find(
    (l) => l.key.length > norm.length && l.key.startsWith(norm)
  );
  if (!hit) return null;
  return {
    type: "template",
    remainder: hit.raw.slice(norm.length),
    full: hit.raw,
  };
}

/**
 * @returns {{ type: "token"|"template", remainder: string, full: string } | null}
 *   `remainder` is what the ghost renders after the typed text;
 *   `full` is the input value after accepting with Tab.
 */
export function getSuggestion(inputValue, grammarPack, rankings, now = new Date()) {
  if (!grammarPack || !rankings) return null;
  const value = String(inputValue || "");
  if (!value.trim()) return null;

  // Mid-token completions only make sense when not ending on a space.
  if (!/\s$/.test(value)) {
    const tokens = value.trim().split(/\s+/);
    const last = tokens[tokens.length - 1];
    const before = tokens.slice(0, -1);

    const hasContract = before.some((t) => findContract(t, grammarPack));
    if (!hasContract) {
      const contract = suggestContract(last, grammarPack, rankings);
      if (contract) {
        const remainder = contract.slice(last.length);
        return {
          type: "token",
          remainder,
          full: value.slice(0, value.length - last.length) + contract,
        };
      }
    }

    const tenorRemainder = suggestTenor(last, before, now);
    if (tenorRemainder) {
      return { type: "token", remainder: tenorRemainder, full: value + tenorRemainder };
    }

    const label = suggestLabel(last, grammarPack, rankings);
    if (label) {
      const typedAlpha = last.match(/[a-z]+$/i)[0];
      const remainder = label.slice(typedAlpha.length);
      if (remainder) {
        return { type: "token", remainder, full: value + remainder };
      }
    }
  }

  return suggestTemplate(value, rankings);
}
